import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';

import { ThunkAPI } from '../../rootReducer';
import apiAsyncThunkCreator, { basicApiAsyncThunkCreator } from '../../../services/apiAsyncThunkCreator';
import { ContentTypes, CORE_SERVICES, HttpMethods } from '../../../services/api';
import { showErrorMessage, showSuccessMessage } from '../../notifications';
import { IArchiveCreditPoolPayload, ICreditsPoolItemResponse, ICreditsPoolItemStore } from './interfaces';

type AccountId = string;

export const fetchCreditsPools: AsyncThunk<ICreditsPoolItemStore[], AccountId, ThunkAPI> = createAsyncThunk(
  'FetchCreditsPools',
  async (accountId: string, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<ICreditsPoolItemResponse[], undefined>({
        endpoint: `credit-pool/company/${accountId}`,
        microservice: CORE_SERVICES.CREDITS_SERVICE,
        authenticated: true,
        method: HttpMethods.Get,
        contentType: ContentTypes.ApplicationJson
      })(undefined, thunk);
      const payload: ICreditsPoolItemStore[] = [];

      if (!Array.isArray(response)) {
        throw new Error('Response is not an array');
      }

      response.forEach((itemInResponse) => {
        const { _id, name, available, pending, isActive, members, finalized, thresholdWarningMessage } = itemInResponse;
        payload.push({
          _id,
          name,
          creditsAvailable: available,
          creditsPending: pending,
          creditsFinalized: finalized,
          thresholdWarningMessage,
          isActive,
          members
        });
      });

      return payload;
    } catch (err) {
      thunk.dispatch(showErrorMessage(err as string || 'An error occurred fetching the credits pools.'));
      return thunk.rejectWithValue(err);
    }
  }
);

export const deleteCreditPool: AsyncThunk<string, string, ThunkAPI> = createAsyncThunk(
  'creditPool/delete',
  async (creditPoolId, thunk) => {
    try {
      await apiAsyncThunkCreator<void, void>({
        endpoint: `credit-pool/delete/${creditPoolId}`,
        microservice: CORE_SERVICES.CREDITS_SERVICE,
        authenticated: true,
        method: HttpMethods.Delete
      })(undefined, thunk);
      thunk.dispatch(showSuccessMessage('credit pool deleted successfully'));
      return creditPoolId;
    } catch (error) {
      thunk.dispatch(showErrorMessage(JSON.stringify(error)));
      return thunk.rejectWithValue(error);
    }
  }
);

export const toggleArchiveCreditPool: AsyncThunk<string, IArchiveCreditPoolPayload, ThunkAPI> = createAsyncThunk(
  'creditPool/archive-unarchive',
  async ({ creditPoolId }, thunk) => {
    try {
      await basicApiAsyncThunkCreator<void, IArchiveCreditPoolPayload>({
        endpoint: 'credit-pool/toggle-active-status',
        microservice: CORE_SERVICES.CREDITS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post
      })({ creditPoolId }, thunk);
      thunk.dispatch(showSuccessMessage('Success!'));
      return creditPoolId;
    } catch (error) {
      thunk.dispatch(showErrorMessage('Failed!'));
      return thunk.rejectWithValue(error);
    }
  }
);
